// @ts-nocheck
import { memo, useCallback, useEffect, useRef } from 'react';
import type { CustomCellEditorProps } from 'ag-grid-react';
import { useGridCellEditor } from 'ag-grid-react';
import { Input } from 'antd';
import { isObject, has, isNull, isUndefined } from 'lodash';
import { ORDER_PIVOT_TABLES } from 'pages/orders/constants';

const KEY_BACKSPACE = 'Backspace';
const KEY_F2 = 'F2';
const KEY_ENTER = 'Enter';
const KEY_TAB = 'Tab';
const KEY_ARROW_UP = 'ArrowUp';
const KEY_ARROW_DOWN = 'ArrowDown';

export default memo(
	({ value, onValueChange, eventKey, stopEditing, data, column, colDef, ...props }: CustomCellEditorProps) => {
		const metaData = colDef.metaData;
		const type = metaData.ColumnType === 'number' || metaData.ColumnType === 'qty' ? 'number' : 'text';

		const refInput = useRef<HTMLInputElement>(null);

		let currentValue = value?.value ?? (value === null || value === 'null' ? '' : value);

		const updateValue = (val: string) => {
			const newValue = isObject(val) && has(val, 'value') ? val.value : val;

			if (type !== 'number') {
				onValueChange(newValue);
				return;
			}

			// Allow empty values (e.g., when clearing the input)
			if (newValue === '') {
				onValueChange('');
				return;
			}

			// Allow valid decimal numbers
			if (/^\d*\.?\d*$/.test(newValue)) {
				onValueChange(newValue);
			}
		};

		useEffect(() => {
			let startValue;
			let highlightAllOnFocus = true;

			if (eventKey === KEY_BACKSPACE) {
				startValue = '';
			} else if (eventKey && eventKey.length === 1) {
				startValue = eventKey;
				highlightAllOnFocus = false;
			} else {
				startValue = value;
				if (eventKey === KEY_F2) {
					highlightAllOnFocus = false;
				}
			}

			if (startValue == null) {
				startValue = '';
			}

			updateValue(startValue);

			const eInput = refInput.current!;
			eInput.focus();
			if (highlightAllOnFocus) {
				eInput.select();
			} else {
				const length = eInput.value ? eInput.value.length : 0;
				if (length > 0) {
					eInput.setSelectionRange(length, length);
				}
			}
		}, []);

		const isLeftOrRight = (event: any) => ['ArrowLeft', 'ArrowRight'].includes(event.key);

		const isBackspace = (event: any) => event.key === KEY_BACKSPACE;
		const isArrowUp = (event: any) => event.key === KEY_ARROW_UP;
		const isArrowDown = (event: any) => event.key === KEY_ARROW_DOWN;
		const finishedEditingPressed = (event: any) => [KEY_ENTER, KEY_TAB].includes(event.key);

		const isCharNumeric = (charStr: string) => /^\d$/.test(charStr);

		const onKeyDown = (event: any) => {
			if (isLeftOrRight(event) || isBackspace(event) || isArrowUp(event) || isArrowDown(event)) {
				event.stopPropagation();
				return;
			}

			if (type === 'number') {
				if (
					!finishedEditingPressed(event) &&
					!isCharNumeric(event.key) &&
					!(event.key === '.' && !currentValue.includes('.'))
				) {
					event.preventDefault?.();
				}
			}

			if (finishedEditingPressed(event)) {
				stopEditing();
			}
		};

		const isCancelBeforeStart = useCallback(() => {
			if (type === 'number') {
				return !!eventKey && eventKey.length === 1 && !'0123456789'.includes(eventKey);
			}
			return false;
		}, [eventKey, type]);

		const isCancelAfterEnd = useCallback(() => {
			if (value == null) return false;
			if (type === 'number') {
				// Removed hardcoded 1000000 limit that was preventing valid RA numbers and other identifiers > 6 digits
				return false;
			}
			if (type === 'text') {
				return typeof value === 'string' && value.length > 1000; // optional constraint
			}
			return false;
		}, [value, type]);

		useGridCellEditor({
			isCancelBeforeStart,
			isCancelAfterEnd,
		});
		return (
			<Input
				className="inline-editing"
				type={ORDER_PIVOT_TABLES.includes(data.AppTableID) ? 'text' : type}
				ref={refInput}
				value={currentValue}
				onChange={(event: any) => updateValue(event.target.value)}
				onKeyDown={onKeyDown}
				min={metaData.tableId === 27 ? null : 0}
				placeholder=""
				step={data['QtyPerShelf'] || data['OrderMultiple']}
				disabled={data.IsReadOnly}
			/>
		);
	}
);
